// Central color palette mapping for KPI Work Types and Categories across the KPI module

export const PALETTE_COLORS = [
  { name: "Blue", bg: "bg-[#1d5bbd]", hex: "#1d5bbd", text: "text-[#1d5bbd]", border: "border-[#1d5bbd]", badgeBg: "bg-blue-50 text-blue-700" },
  { name: "Black", bg: "bg-[#1e1e1e]", hex: "#1e1e1e", text: "text-[#1e1e1e]", border: "border-[#1e1e1e]", badgeBg: "bg-slate-100 text-slate-800" },
  { name: "Emerald", bg: "bg-[#0e7b56]", hex: "#0e7b56", text: "text-[#0e7b56]", border: "border-[#0e7b56]", badgeBg: "bg-emerald-50 text-emerald-700" },
  { name: "Indigo", bg: "bg-[#4c51bf]", hex: "#4c51bf", text: "text-[#4c51bf]", border: "border-[#4c51bf]", badgeBg: "bg-indigo-50 text-indigo-700" },
  { name: "Ochre", bg: "bg-[#b45309]", hex: "#b45309", text: "text-[#b45309]", border: "border-[#b45309]", badgeBg: "bg-amber-50 text-amber-800" },
  { name: "Purple", bg: "bg-[#7c3aed]", hex: "#7c3aed", text: "text-[#7c3aed]", border: "border-[#7c3aed]", badgeBg: "bg-purple-50 text-purple-700" },
  { name: "Teal", bg: "bg-[#009688]", hex: "#009688", text: "text-[#009688]", border: "border-[#009688]", badgeBg: "bg-teal-50 text-teal-700" },
  { name: "Gray", bg: "bg-[#71717a]", hex: "#71717a", text: "text-[#71717a]", border: "border-[#71717a]", badgeBg: "bg-slate-100 text-slate-600" },
  { name: "Rose", bg: "bg-[#e11d48]", hex: "#e11d48", text: "text-[#e11d48]", border: "border-[#e11d48]", badgeBg: "bg-rose-50 text-rose-700" },
  { name: "Cyan", bg: "bg-[#0891b2]", hex: "#0891b2", text: "text-[#0891b2]", border: "border-[#0891b2]", badgeBg: "bg-cyan-50 text-cyan-700" },
  { name: "Orange", bg: "bg-[#ea580c]", hex: "#ea580c", text: "text-[#ea580c]", border: "border-[#ea580c]", badgeBg: "bg-orange-50 text-orange-700" },
  { name: "Lime", bg: "bg-[#65a30d]", hex: "#65a30d", text: "text-[#65a30d]", border: "border-[#65a30d]", badgeBg: "bg-lime-50 text-lime-700" },
];

export const KPI_TYPE_COLORS = PALETTE_COLORS;

const KNOWN_TYPE_MAPPINGS = {
  "client meetings": PALETTE_COLORS[0], // Blue
  "estimation": PALETTE_COLORS[5],       // Purple
  "estimations": PALETTE_COLORS[1],      // Black
  "bug fix": PALETTE_COLORS[8],          // Rose Red
  "data entry": PALETTE_COLORS[4],       // Ochre Brown
  "task": PALETTE_COLORS[3],             // Indigo Blue
  "sprint planning": PALETTE_COLORS[2],  // Emerald Green
  "r&d": PALETTE_COLORS[3],              // Indigo
  "documentation": PALETTE_COLORS[4],    // Ochre
  "hiring": PALETTE_COLORS[5],           // Purple
  "mentoring": PALETTE_COLORS[6],        // Teal
  "blog / r&d": PALETTE_COLORS[7],       // Gray
  "feature": PALETTE_COLORS[0],          // Blue
  "code review": PALETTE_COLORS[2],      // Emerald Green
};

export const getKpiTypeColor = (typeNameOrId, indexFallback = 0) => {
  if (!typeNameOrId) return PALETTE_COLORS[indexFallback % PALETTE_COLORS.length];

  if (typeof typeNameOrId === "object" && (typeNameOrId?.bg_color || typeNameOrId?.color)) {
    const hex = typeNameOrId.bg_color || typeNameOrId.color;
    return {
      name: typeNameOrId.name || typeNameOrId.kpi_type_name || typeNameOrId.category_name || "Custom",
      bg: `bg-[${hex}]`,
      hex: hex,
      text: `text-[${hex}]`,
      border: `border-[${hex}]`,
      badgeBg: `bg-[${hex}]/10 text-[${hex}]`
    };
  }

  if (typeof typeNameOrId === "string" && typeNameOrId.startsWith("#")) {
    const hex = typeNameOrId;
    return {
      name: "Custom",
      bg: `bg-[${hex}]`,
      hex: hex,
      text: `text-[${hex}]`,
      border: `border-[${hex}]`,
      badgeBg: `bg-[${hex}]/10 text-[${hex}]`
    };
  }

  const nameStr = typeof typeNameOrId === "object" ? (typeNameOrId.name || typeNameOrId.title || "") : String(typeNameOrId);
  const cleanKey = nameStr.trim().toLowerCase();

  if (KNOWN_TYPE_MAPPINGS[cleanKey]) {
    return KNOWN_TYPE_MAPPINGS[cleanKey];
  }

  let hash = 0;
  for (let i = 0; i < cleanKey.length; i++) {
    hash = cleanKey.charCodeAt(i) + ((hash << 5) - hash);
  }
  const idx = Math.abs(hash + indexFallback) % PALETTE_COLORS.length;
  return PALETTE_COLORS[idx];
};

export const AVATAR_PALETTE = [
  "bg-[#1d5bbd]", // Blue
  "bg-[#147b56]", // Emerald Green
  "bg-[#ab6919]", // Ochre / Amber
  "bg-[#6558e8]", // Purple
  "bg-[#c73838]", // Rose Red
  "bg-[#0891b2]", // Cyan
  "bg-[#ea580c]", // Orange
  "bg-[#0e7b56]", // Teal
  "bg-[#7c3aed]", // Deep Violet
  "bg-[#b45309]", // Dark Amber
];

export const getUserAvatarColor = (nameOrId, indexFallback = 0) => {
  if (!nameOrId) return AVATAR_PALETTE[indexFallback % AVATAR_PALETTE.length];
  
  const str = typeof nameOrId === "object"
    ? (nameOrId._id || nameOrId.full_name || nameOrId.name || nameOrId.email || "")
    : String(nameOrId);
    
  if (!str) return AVATAR_PALETTE[indexFallback % AVATAR_PALETTE.length];

  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  const idx = Math.abs(hash + indexFallback) % AVATAR_PALETTE.length;
  return AVATAR_PALETTE[idx];
};

export const formatQuarterRange = (item, index = 0, period = "") => {
  if (!item) return null;
  let qNum = item.quarter || item.q;
  const combinedStr = `${item.label || ''} ${item.name || ''} ${item.month || ''} ${item.title || ''}`;
  if (!qNum && combinedStr) {
    const match = combinedStr.match(/q(?:uarter)?\s*([1-4])/i);
    if (match) {
      qNum = parseInt(match[1], 10);
    } else if (/^[1-4]$/.test(combinedStr.trim())) {
      qNum = parseInt(combinedStr.trim(), 10);
    }
  }
  if (!qNum && period === "quarter") {
    qNum = (index % 4) + 1;
  }

  // 1. If API provides explicit month string (e.g. "Sep, Oct, Nov" or "Sep - Nov" or "Dec, Jan, Feb")
  if (item.month && typeof item.month === "string") {
    const rawMonth = item.month.trim();
    if (rawMonth.includes(",")) {
      const months = rawMonth.split(",").map(m => m.trim()).filter(Boolean);
      if (months.length >= 2) {
        const first = months[0].split(/\s+/)[0].slice(0, 3);
        const last = months[months.length - 1].split(/\s+/)[0].slice(0, 3);
        return {
          range: `${first} - ${last}`,
          quarterNum: qNum || (index % 4) + 1
        };
      }
    } else if (rawMonth.includes("-")) {
      return {
        range: rawMonth,
        quarterNum: qNum || (index % 4) + 1
      };
    }
  }

  // 2. Fallback to default quarter month ranges if no month string is provided by API
  const quarterRanges = {
    1: "Jan - Mar",
    2: "Apr - Jun",
    3: "Jul - Sep",
    4: "Oct - Dec",
  };

  if (qNum && quarterRanges[qNum]) {
    return {
      range: quarterRanges[qNum],
      quarterNum: qNum
    };
  }

  return null;
};
